import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useTenantSettings } from "@/hooks/useTenantSettings";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import {
  Printer,
  Share2,
  Download,
  MessageCircle,
  Check,
  Receipt,
  FileText,
} from "lucide-react";
import { format } from "date-fns";
import { generateWhatsAppInvoiceLink } from "@/lib/whatsapp";
import { downloadA4InvoicePDF, printA4InvoicePDF } from "@/lib/generateA4InvoicePDF";
import { downloadThermalInvoicePDF, printThermalInvoicePDF } from "@/lib/generateThermalInvoicePDF";
import { PrintOptionsDialog } from "@/components/sales/PrintOptionsDialog";

interface InvoiceItem {
  serviceName: string;
  quantity: number;
  unitPrice: number;
  originalPrice?: number;
  discount?: number;
  totalPrice: number;
}

interface InvoiceData {
  id: string;
  invoiceNumber?: string;
  customerName?: string;
  customerPhone?: string;
  items: InvoiceItem[];
  totalAmount: number;
  totalDiscount?: number;
  receivedAmount: number;
  dueAmount: number;
  paymentMode: string;
  paymentStatus: string;
  createdAt: string;
}

interface InvoicePreviewPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: InvoiceData | null;
}

export function InvoicePreviewPopup({ open, onOpenChange, invoice }: InvoicePreviewPopupProps) {
  const isMobile = useIsMobile();
  const { tenant } = useAuth();
  const { settings } = useTenantSettings();
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [showPrintOptions, setShowPrintOptions] = useState(false);

  if (!invoice) return null;

  const formatCurrency = (value: number) => `₹${value.toLocaleString('en-IN')}`;

  const getInvoiceData = () => ({
    invoice_number: invoice.invoiceNumber || `INV-${invoice.id.slice(0, 8).toUpperCase()}`,
    customer_name: invoice.customerName || "Walk-in Customer",
    customer_phone: invoice.customerPhone || null,
    total_amount: invoice.totalAmount,
    total_discount: invoice.totalDiscount || 0,
    received_amount: invoice.receivedAmount,
    due_amount: invoice.dueAmount,
    payment_mode: invoice.paymentMode,
    payment_status: invoice.paymentStatus,
    created_at: invoice.createdAt,
    notes: null,
  });

  const getInvoiceItems = () => invoice.items.map(item => ({
    service_name: item.serviceName,
    quantity: item.quantity,
    unit_price: item.originalPrice || item.unitPrice,
    discount: item.discount || 0,
    total_price: item.totalPrice,
  }));

  const getTenantData = () => ({
    name: tenant?.name || "VLE Plus",
    phone: tenant?.phone,
    email: tenant?.email,
    address: tenant?.address,
    gstin: settings?.gst_number || undefined,
    logo_url: tenant?.shop_logo_url || tenant?.logo_url,
    vle_id: tenant?.vle_id,
  });

  const getInvoiceSettings = () => ({
    gst_enabled: tenant?.gst_enabled || false,
    apply_gst_on_invoices: tenant?.apply_gst_on_invoices || false,
    cgst_percent: tenant?.cgst_percentage || 0,
    sgst_percent: tenant?.sgst_percentage || 0,
    show_logo: settings?.show_logo_on_invoice ?? true,
    bank_details: settings?.bank_details,
    upi_id: settings?.upi_id,
    terms_conditions: settings?.terms_conditions,
    invoice_footer: settings?.invoice_footer,
    qr_code_url: settings?.qr_code_url,
    signature_url: settings?.signature_url,
    stamp_enabled: settings?.stamp_enabled ?? true,
    invoice_services: settings?.invoice_services || [],
    branding_text: settings?.branding_text,
    branding_enabled: settings?.branding_enabled ?? true,
  });

  const handlePrintOption = async (type: 'thermal' | 'a4', action: 'print' | 'download') => {
    const sale = getInvoiceData();
    const items = getInvoiceItems();
    const tenantData = getTenantData();
    const invoiceSettings = getInvoiceSettings();

    // GST settings for thermal/standard invoices
    const thermalSettings = {
      invoice_footer: invoiceSettings.invoice_footer,
      gst_enabled: tenant?.gst_enabled || false,
      apply_gst_on_invoices: tenant?.apply_gst_on_invoices || false,
      cgst_percentage: tenant?.cgst_percentage || 0,
      sgst_percentage: tenant?.sgst_percentage || 0,
    };

    try {
      if (type === 'a4') {
        if (action === 'print') {
          await printA4InvoicePDF(sale, items, tenantData, invoiceSettings);
          toast.success("A4 invoice sent to printer");
        } else {
          await downloadA4InvoicePDF(sale, items, tenantData, invoiceSettings);
          toast.success("A4 invoice PDF downloaded");
        }
      } else {
        if (action === 'print') {
          printThermalInvoicePDF(sale, items, tenantData, thermalSettings);
          toast.success("Thermal receipt sent to printer");
        } else {
          downloadThermalInvoicePDF(sale, items, tenantData, thermalSettings);
          toast.success("Thermal receipt PDF downloaded");
        }
      }
    } catch (error) {
      console.error('Print/Download error:', error);
      toast.error("Failed to generate invoice");
    }
  };

  const handleShareWhatsApp = () => {
    if (!invoice.customerPhone) {
      toast.error("Customer phone number not available");
      return;
    }

    const url = generateWhatsAppInvoiceLink({
      customerName: invoice.customerName,
      customerPhone: invoice.customerPhone,
      businessName: tenant?.name || "VLE Plus",
      invoiceNumber: invoice.invoiceNumber || `INV-${invoice.id.slice(0, 8).toUpperCase()}`,
      totalAmount: invoice.totalAmount,
      date: invoice.createdAt,
    });

    if (url) {
      window.open(url, "_blank");
      toast.success("Opening WhatsApp...");
    } else {
      toast.error("Invalid phone number");
    }
  };

  const handleShare = async () => {
    const shareText = `Invoice ${invoice.invoiceNumber || invoice.id.slice(0, 8)} - Total: ${formatCurrency(invoice.totalAmount)}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Invoice",
          text: shareText,
        });
      } catch (error) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      toast.success("Invoice details copied to clipboard");
    }
  };

  const getStatusBadge = () => {
    switch (invoice.paymentStatus) {
      case "paid":
        return <Badge className="bg-green-500/10 text-green-600 border-0">Paid</Badge>;
      case "partial":
        return <Badge className="bg-amber-500/10 text-amber-600 border-0">Partial</Badge>;
      default:
        return <Badge className="bg-red-500/10 text-red-600 border-0">Unpaid</Badge>;
    }
  };

  const content = (
    <div className="space-y-4">
      {/* Success Header */}
      <div className="text-center py-4 bg-green-500/10 rounded-xl">
        <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-green-600">Sale Complete!</h2>
        <p className="text-sm text-muted-foreground">Invoice generated successfully</p>
      </div>

      {/* Invoice Preview */}
      <Card className="p-4" ref={invoiceRef}>
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-bold text-lg">{tenant?.name || "VLE Plus"}</h3>
            {tenant?.address && <p className="text-xs text-muted-foreground">{tenant.address}</p>}
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Invoice</p>
            <p className="font-mono font-semibold">
              {invoice.invoiceNumber || `INV-${invoice.id.slice(0, 8).toUpperCase()}`}
            </p>
          </div>
        </div>

        <Separator className="my-3" />

        {/* Customer & Date */}
        <div className="flex justify-between text-sm mb-4">
          <div>
            <p className="text-muted-foreground">Customer</p>
            <p className="font-medium">{invoice.customerName || "Walk-in"}</p>
            {invoice.customerPhone && (
              <p className="text-xs text-muted-foreground">{invoice.customerPhone}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-muted-foreground">Date</p>
            <p className="font-medium">{format(new Date(invoice.createdAt), "dd/MM/yyyy")}</p>
            <p className="text-xs text-muted-foreground">{format(new Date(invoice.createdAt), "hh:mm a")}</p>
          </div>
        </div>

        <Separator className="my-3" />

        {/* Items */}
        <div className="space-y-2 mb-4">
          {invoice.items.map((item, index) => (
            <div key={index} className="flex justify-between text-sm">
              <div>
                <span className="font-medium">{item.serviceName}</span>
                {item.quantity > 1 && (
                  <span className="text-muted-foreground"> x{item.quantity}</span>
                )}
              </div>
              <span className="font-medium">{formatCurrency(item.totalPrice)}</span>
            </div>
          ))}
        </div>

        <Separator className="my-3" />

        {/* Totals */}
        <div className="space-y-2">
          {(() => {
            const subtotalBeforeDiscount = invoice.items.reduce((sum, item) => sum + (item.originalPrice || item.unitPrice) * item.quantity, 0);
            const totalDiscount = invoice.items.reduce((sum, item) => sum + (item.discount || 0), 0);
            
            return (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(subtotalBeforeDiscount)}</span>
                </div>
                {totalDiscount > 0 && (
                  <div className="flex justify-between text-sm text-orange-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(totalDiscount)}</span>
                  </div>
                )}
              </>
            );
          })()}
          <div className="flex justify-between text-lg font-bold">
            <span>Total</span>
            <span>{formatCurrency(invoice.totalAmount)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Received ({invoice.paymentMode})</span>
            <span className="text-green-600">{formatCurrency(invoice.receivedAmount)}</span>
          </div>
          {invoice.dueAmount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Balance Due</span>
              <span className="text-red-600">{formatCurrency(invoice.dueAmount)}</span>
            </div>
          )}
          <div className="flex justify-end">
            {getStatusBadge()}
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" className="h-12 gap-2" onClick={() => setShowPrintOptions(true)}>
          <Printer className="h-5 w-5" />
          Print
        </Button>
        <Button variant="outline" className="h-12 gap-2" onClick={() => setShowPrintOptions(true)}>
          <Download className="h-5 w-5" />
          Download
        </Button>
        {invoice.customerPhone && (
          <Button variant="outline" className="h-12 gap-2 col-span-2 bg-green-500/10 hover:bg-green-500/20 text-green-600 border-green-500/30" onClick={handleShareWhatsApp}>
            <MessageCircle className="h-5 w-5" />
            Share on WhatsApp
          </Button>
        )}
        <Button variant="outline" className="h-12 gap-2" onClick={handleShare}>
          <Share2 className="h-5 w-5" />
          Share
        </Button>
        <Button className="h-12 gap-2" onClick={() => onOpenChange(false)}>
          <Receipt className="h-5 w-5" />
          New Sale
        </Button>
      </div>

      {/* Print Options Dialog */}
      <PrintOptionsDialog
        open={showPrintOptions}
        onOpenChange={setShowPrintOptions}
        onSelectOption={handlePrintOption}
      />
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[95vh] p-4">
          <DrawerHeader className="p-0 pb-2">
            <DrawerTitle className="sr-only">Invoice Preview</DrawerTitle>
          </DrawerHeader>
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-6">
        <DialogHeader className="sr-only">
          <DialogTitle>Invoice Preview</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
